import { actions as bookmakersInfoActions } from '../bookmakersInfo';
import { requestLogout } from '../loginPage/login.actions';
import { actions as modalActions } from '../../modals';
import {
    GENERIC_ERROR,
    ENTER_STAKE,
    BOG_UNAVAILABLE,
} from '../globalModal/modalMessages';
import ApiClient from '../utils/apiClient';
import {
    isUnknownError,
    isGenericError,
    isBogUnavailable,
    ERROR_CODES,
    ERROR_MESSAGES,
    getErrorsToFire,
} from '../utils/apiErrors';
import convertFractionalPrice from '../utils/convertFractionalPrice';
import { isEmptyStake } from '../utils/calculateStake';
import updateAuthToken from '../utils/updateAuthToken';
import { getCookie } from '../utils/cookie';

export const actionTypes = {
    PLACE_BET_REQUEST: 'PLACE_BET_REQUEST',
    PLACE_BET_SUCCESS: 'PLACE_BET_SUCCESS',
    CLEAR_PLACED_BET_DETAILS: 'CLEAR_PLACED_BET_DETAILS',
    SET_BET_OBSTRUCTION: 'SET_BET_OBSTRUCTION',
    CLEAR_BET_OBSTRUCTION: 'CLEAR_BET_OBSTRUCTION',
};

export const betObstructions = {
    NONE: 'NONE',
    REMOVE: 'REMOVE',
    ACCEPT: 'ACCEPT',
    ACCEPT_AND_REMOVE: 'ACCEPT_AND_REMOVE',
};

export const placeBetRequest = isPlacingBet => ({
    type: actionTypes.PLACE_BET_REQUEST,
    isPlacingBet,
});

const placeBetSuccess = placedBetDetails => ({
    type: actionTypes.PLACE_BET_SUCCESS,
    placedBetDetails,
});

export const clearPlacedBetDetails = () => ({
    type: actionTypes.CLEAR_PLACED_BET_DETAILS,
});

export const setBetObstruction = obstruction => (dispatch) => {
    dispatch({
        type: actionTypes.SET_BET_OBSTRUCTION,
        obstruction,
    });
};

export const clearBetObstruction = () => ({
    type: actionTypes.CLEAR_BET_OBSTRUCTION,
})

const handlePlaceBetResponse = (
    placeBetResponse,
    selectedBets,
    multiples,
    freeBet,
) => (dispatch) => {
    const receipts = placeBetResponse.data
    const containsPending = receipts.some(receipt => receipt.attributes.status === 'pending')
    const placedBetDetails = {
        containsPending,
        freeBet,
        bets: [],
    }

    placedBetDetails.bets = receipts.map((receipt) => {
        const {
            id,
            attributes,
        } = receipt

        const {
            betId,
            stakePerLine,
            numberLines,
            betType,
            potentialReturns,
            betSelections,
        } = attributes

        const placedBetSelections = betSelections.map(
            betSelection => selectedBets.find(selectedBet => (
                selectedBet.outcomersId === betSelection.selectionId
            )),
        )

        const placedMultiple = multiples.find(multiple => (
            multiple.optionId === betId.toString()
        ))

        return {
            bets: placedBetSelections,
            receiptId: id,
            betId,
            stakePerLine,
            betType,
            potentialReturns,
            multiple: placedMultiple,
            numberLines,
        }
    })

    dispatch(placeBetSuccess(placedBetDetails));
    dispatch(bookmakersInfoActions.clearSelectedBets());
    dispatch(bookmakersInfoActions.clearMultiples());
}

export const betSuspended = (dispatch, origin, betError) => {
    const meta = betError.meta
    const outcomersId = meta.selection.selectionId.toString()

    if (origin === 'BET') {
        dispatch(bookmakersInfoActions.setSuspendedBet(
            outcomersId,
            true,
        ));
        dispatch(bookmakersInfoActions.setError(
            outcomersId,
            betError.code,
            ERROR_MESSAGES.BETTING_SUSPENDED,
        ));
    } else {
        dispatch(bookmakersInfoActions.setSuspendedBetOnBuild(
            outcomersId,
            true,
        ));
        dispatch(bookmakersInfoActions.setErrorOnBuild(
            outcomersId,
            betError.code,
            ERROR_MESSAGES.BETTING_SUSPENDED,
        ));
    }
}

const toTwoDecimalPlaces = number => (
    parseFloat(Math.round(number * 100) / 100).toFixed(2)
)

const stakeTooLow = (dispatch, origin, betError, currencySign) => {
    const meta = betError.meta
    const minStake = toTwoDecimalPlaces(meta.minStake)
    const message = ERROR_MESSAGES.STAKE_TOO_LOW
        .replace('%PRICE%', `${currencySign}${minStake}`)

    if (meta.bet.betType === 'single') {
        const outcomersId = meta.bet.betSelections[0].selectionId.toString()
        if (origin === 'BET') {
            dispatch(bookmakersInfoActions.setError(
                outcomersId,
                betError.code,
                message,
            ))
        } else {
            dispatch(bookmakersInfoActions.setErrorOnBuild(
                outcomersId,
                betError.code,
                message,
            ))
        }
    } else {
        const optionId = meta.bet.id
        dispatch(bookmakersInfoActions.setMultiplesError(
            optionId,
            betError.code,
            message,
        ))
    }
}

const stakeTooHigh = (dispatch, origin, betError, currencySign) => {
    const meta = betError.meta
    const maxStake = toTwoDecimalPlaces(meta.maxStake)
    const message = ERROR_MESSAGES.STAKE_TOO_HIGH
        .replace('%PRICE%', `${currencySign}${maxStake}`)

    if (meta.bet.betType === 'single') {
        const outcomersId = meta.bet.betSelections[0].selectionId.toString()
        if (origin === 'BET') {
            dispatch(bookmakersInfoActions.setError(
                outcomersId,
                betError.code,
                message,
            ))
        } else {
            dispatch(bookmakersInfoActions.setErrorOnBuild(
                outcomersId,
                betError.code,
                message,
            ))
        }
    } else {
        const optionId = meta.bet.id
        dispatch(bookmakersInfoActions.setMultiplesError(
            optionId,
            betError.code,
            message,
        ))
    }
}

/**
 * Deppending on the odds type, convert the price to decimal or fractional
 * 
 * @param {Boolean} isDecimalOdds 
 * @param {String} decimalPrice 
 * @param {String} fractionalPrice 
 * @returns price
 */
const convertPriceByOdds = (isDecimalOdds, decimalPrice, fractionalPrice) => {
    const price = isDecimalOdds ? Number(decimalPrice).toFixed(2) : fractionalPrice;
    
    return price;
}

export const priceChanged = (dispatch, origin, betError) => {
    const customizeSettings = getCookie('customizeSettings');

    const userSettings = customizeSettings 
        ? JSON.parse(customizeSettings)
        : { decimalOdds: false };
    
    const meta = betError.meta
    const outcomersId = meta.selection.selectionId.toString()
    const newPriceNumerator = meta.priceNumerator
    const newPriceDenominator = meta.priceDenominator
    const newFractionalPrice = `${newPriceNumerator}/${newPriceDenominator}`;
    const newDecimalPrice = convertFractionalPrice(newFractionalPrice);
    const oldPriceNumerator = meta.selection.priceNumerator
    const oldPriceDenominator = meta.selection.priceDenominator

    const oldFractionalPrice = `${oldPriceNumerator}/${oldPriceDenominator}`;
    const oldDecimalPrice = convertFractionalPrice(oldFractionalPrice);
            
    if (newPriceNumerator !== oldPriceNumerator ||
        newPriceDenominator !== oldPriceDenominator) {
        let message;
        if (meta.selection.priceNumerator === null) {
            dispatch(bookmakersInfoActions.updateBetPrice(
                outcomersId,
                newFractionalPrice,
                newDecimalPrice,
            ))
            message = ERROR_MESSAGES.PRICE_CHANGED_SP;
        } else {
            message = ERROR_MESSAGES.PRICE_CHANGED
                .replace('%OLD%', 
                    convertPriceByOdds(userSettings.decimalOdds, oldDecimalPrice, oldFractionalPrice))
                .replace('%NEW%', 
                    convertPriceByOdds(userSettings.decimalOdds, newDecimalPrice, newFractionalPrice))
        }

        // Attach new price to associated bet (to be accepted by cta)
        if (origin === 'BET') {
            dispatch(bookmakersInfoActions.setError(
                outcomersId,
                betError.code,
                message,
            ))
            dispatch(bookmakersInfoActions.setAssociatedPriceChange(
                outcomersId,
                newFractionalPrice,
                newDecimalPrice,
            ))
        } else {
            dispatch(bookmakersInfoActions.setErrorOnBuild(
                outcomersId,
                betError.code,
                message,
            ))
            dispatch(bookmakersInfoActions.setAssociatedPriceChangeOnBuild(
                outcomersId,
                newFractionalPrice,
                newDecimalPrice,
            ))
        }
    }
}

const insufficientFunds = (dispatch, origin, betError) => {
    if (origin === 'BET') {
        dispatch(bookmakersInfoActions.setGenericError(
            betError.code,
            ERROR_MESSAGES.INSUFFICIENT_FUNDS,
        ))
    } else {
        dispatch(bookmakersInfoActions.setGenericErrorOnBuild(
            betError.code,
            ERROR_MESSAGES.INSUFFICIENT_FUNDS,
        ))
    }
}

const startingPriceUnavailable = (dispatch, origin, betError) => {
    const meta = betError.meta
    const outcomersId = meta.bet.betSelections[0].selectionId.toString()

    if (origin === 'BET') {
        dispatch(bookmakersInfoActions.setError(
            outcomersId,
            betError.code,
            'SP is no longer available for this race',
        ))
    } else {
        dispatch(bookmakersInfoActions.setErrorOnBuild(
            outcomersId,
            betError.code,
            'SP is no longer available for this race',
        ))
    }
    dispatch(bookmakersInfoActions.updateAllStartingPricesAvailability())
}

export const updateObstructions = errors => (dispatch) => {
    const isStartingPriceUnavailable = errors.includes(ERROR_CODES.STARTING_PRICE_UNAVAILABLE);
    const isBetSuspended = errors.includes(ERROR_CODES.BETTING_SUSPENDED);
    const isPriceChanged = errors.includes(ERROR_CODES.PRICE_CHANGED);
    const priceAffected = isPriceChanged || isStartingPriceUnavailable

    switch (true) {
        case isBetSuspended && priceAffected:
            dispatch(setBetObstruction(betObstructions.ACCEPT_AND_REMOVE))
            break
        case priceAffected:
            dispatch(setBetObstruction(betObstructions.ACCEPT))
            break
        case isBetSuspended:
            dispatch(setBetObstruction(betObstructions.REMOVE))
            break
        default:
            dispatch(clearBetObstruction())
    }
}

export const fireError = (dispatch, origin, betError, currencySign) => {
    switch (betError.code) {
        case ERROR_CODES.BETTING_SUSPENDED:
            betSuspended(dispatch, origin, betError)
            break
        case ERROR_CODES.STAKE_TOO_LOW:
            stakeTooLow(dispatch, origin, betError, currencySign)
            break
        case ERROR_CODES.STAKE_TOO_HIGH:
            stakeTooHigh(dispatch, origin, betError, currencySign)
            break
        case ERROR_CODES.PRICE_CHANGED:
            priceChanged(dispatch, origin, betError)
            break
        case ERROR_CODES.INSUFFICIENT_FUNDS:
            insufficientFunds(dispatch, origin, betError)
            break
        case ERROR_CODES.STARTING_PRICE_UNAVAILABLE:
            startingPriceUnavailable(dispatch, origin, betError)
            break
        default:
    }
}

export const placeBet = (
    bookmaker,
    selectedBets,
    multiples,
    selectedFreeBet,
    feed,
    currencySign,
    isExternalLogout,
) => (dispatch) => {
    // Flag that the place bet request has started (spinner starts)
    dispatch(placeBetRequest(true))

    // Clear any generic errors
    dispatch(bookmakersInfoActions.clearGenericError())

    // Clear all existing bet-specific errors
    selectedBets.forEach((selectedBet) => {
        dispatch(bookmakersInfoActions.clearError(
            selectedBet.outcomersId.toString(),
        ))
    })

    const singlesWithEmptyStake = selectedBets.filter(
        bet => isEmptyStake(bet.stake),
    )
    const multiplesWithEmptyStake = multiples.filter(
        multiple => isEmptyStake(multiple.stakeValue),
    )
    const allSinglesEmpty = singlesWithEmptyStake.length === selectedBets.length
    const allMultiplesEmpty = multiplesWithEmptyStake.length === multiples.length
    if (allSinglesEmpty && allMultiplesEmpty) {
        // If all stakes are empty, throw a global modal
        dispatch(modalActions.setGlobalModal(ENTER_STAKE))

        return Promise.reject()
    }

    // Set un-used bets so that they can be retrieved via Re-Use Selection
    const unusedBets = selectedBets.filter(
        bet => isEmptyStake(bet.stake),
    )
    dispatch(bookmakersInfoActions.setUnusedBets(unusedBets))

    // Auto-logout if the user has been logged out from the bookmaker
    const onUnauthorized = requestLogout(bookmaker, feed, isExternalLogout)

    // Filter _staked_ singles and multiples for placement
    const stakedMultiples = multiples.filter(
        multiple => !isEmptyStake(multiple.stakeValue),
    )

    const selectedFreeBetId = selectedFreeBet ? selectedFreeBet.id : null

    // Note that we still require un-staked singles
    return ApiClient.placeBet(
        bookmaker,
        selectedBets,
        stakedMultiples,
        selectedFreeBetId,
        dispatch,
        onUnauthorized,
    )
        .then((responseBody) => {
            updateAuthToken(responseBody.headers);
            dispatch(
                handlePlaceBetResponse(
                    responseBody.data,
                    selectedBets,
                    stakedMultiples,
                    selectedFreeBet,
                ),
            )
        })
        .catch((error) => {
            if (isUnknownError(error)) {
                dispatch(modalActions.setGlobalModal(GENERIC_ERROR));
                return Promise.reject()
            }

            const body = error.response.data

            if (isGenericError(body)) {
                const message = isBogUnavailable(body)
                    ? BOG_UNAVAILABLE
                    : GENERIC_ERROR
                dispatch(modalActions.setGlobalModal(message))
                return Promise.reject()
            }

            const errorsToFire = getErrorsToFire(body.errors)
            errorsToFire.map(errorToFire => fireError(dispatch, 'BET', errorToFire, currencySign))

            // Update CTA obstruction according to errors that were fired
            const errorCodes = errorsToFire.map(errorToFire => errorToFire.code)
            dispatch(updateObstructions(errorCodes))

            return Promise.reject()
        })
};



// WEBPACK FOOTER //
// /src/js/common/bet/bet.actions.js